import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import LandingNav from "@/components/LandingNav";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/Footer";
import {
  Rocket,
  Chrome,
  Monitor,
  Smartphone,
  Code,
  UserPlus,
  Database,
  Globe,
  Sparkles,
} from "lucide-react";

const HowItWorks = () => {
  const { t } = useTranslation();

  const platforms = [
    {
      icon: Chrome,
      title: t("howItWorks.platforms.browser.title"),
      description: t("howItWorks.platforms.browser.description"),
    },
    {
      icon: Monitor,
      title: t("howItWorks.platforms.desktop.title"),
      description: t("howItWorks.platforms.desktop.description"),
    },
    {
      icon: Smartphone,
      title: t("howItWorks.platforms.mobile.title"),
      description: t("howItWorks.platforms.mobile.description"),
    },
    {
      icon: Code,
      title: t("howItWorks.platforms.api.title"),
      description: t("howItWorks.platforms.api.description"),
    },
    {
      icon: Sparkles,
      title: t("howItWorks.platforms.mcp.title"),
      description: t("howItWorks.platforms.mcp.description"),
    },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="How It Works – TestAssets"
        description="Learn how TestAssets helps you manage and paste test data across browser extensions, desktop apps, mobile, and REST APIs."
        path="/how-it-works"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "How It Works", url: "/how-it-works" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to use TestAssets",
          description: "Get started with TestAssets in three simple steps.",
          step: [
            { "@type": "HowToStep", position: 1, name: t("landing.howItWorks.step1.title"), text: t("landing.howItWorks.step1.description") },
            { "@type": "HowToStep", position: 2, name: t("landing.howItWorks.step2.title"), text: t("landing.howItWorks.step2.description") },
            { "@type": "HowToStep", position: 3, name: t("landing.howItWorks.step3.title"), text: t("landing.howItWorks.step3.description") },
          ],
        }}
      />
      <LandingNav />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden" aria-labelledby="hiw-hero-heading">
          <div
            className="absolute inset-0 opacity-90"
            style={{
              background: "linear-gradient(135deg, hsl(0 40% 45%), hsl(250 50% 40%))",
            }}
          />
          <div className="relative container mx-auto px-6 py-20 md:py-28">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h1 id="hiw-hero-heading" className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                {t("howItWorks.hero.title")}
              </h1>
              <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
                {t("howItWorks.hero.description")}
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Steps */}
        <section className="py-24 bg-card" aria-labelledby="hiw-steps-heading">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="hiw-steps-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t("landing.howItWorks.title")}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {t("landing.howItWorks.description")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { icon: UserPlus, step: "1", title: t("landing.howItWorks.step1.title"), description: t("landing.howItWorks.step1.description") },
                { icon: Database, step: "2", title: t("landing.howItWorks.step2.title"), description: t("landing.howItWorks.step2.description") },
                { icon: Globe, step: "3", title: t("landing.howItWorks.step3.title"), description: t("landing.howItWorks.step3.description") },
              ].map((item, index) => {
                const StepIcon = item.icon;
                return (
                  <div key={index} className="flex flex-col items-center text-center gap-4">
                    <div className="relative">
                      <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <StepIcon className="h-8 w-8 text-primary" />
                      </div>
                      <span className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">
                        {item.step}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="py-24 bg-background" aria-labelledby="hiw-platforms-heading">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="hiw-platforms-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t("howItWorks.platformsTitle")}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t("howItWorks.platformsDescription")}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {platforms.map((platform, index) => {
                const PlatformIcon = platform.icon;
                return (
                  <Card
                    key={index}
                    className="p-6 bg-card border-border hover:border-primary/50 transition-all duration-300 text-center"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <PlatformIcon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-card-foreground">
                        {platform.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {platform.description}
                      </p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("howItWorks.cta.title")}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              {t("howItWorks.cta.description")}
            </p>
            <Button
              size="lg"
              asChild
              className="rounded-lg text-lg px-8 py-6"
            >
              <Link to="/auth">
                <Rocket className="h-5 w-5" />
                {t("howItWorks.cta.button")}
              </Link>
            </Button>
            <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/applications" className="text-primary hover:underline">
                {t("applications.title", "Applications & Integrations")} →
              </Link>
              <Link to="/pricing" className="text-primary hover:underline">
                {t("pricing.title", "Pricing")} →
              </Link>
              <Link to="/mcp-docs" className="text-primary hover:underline">
                {t("howItWorks.platforms.mcp.title")} →
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowItWorks;
